'use client';

import React, { useState, useRef } from 'react';
import { X, Plus, MessageCircleHeart, MoreHorizontal, Pencil, Trash2, Check, ArrowRight } from 'lucide-react';
import type { Conversation } from './ChatScreen';

interface Props {
  conversations: Conversation[];
  activeId: string;
  onSelect: (id: string) => void;
  isOpen: boolean;
  onClose: () => void;
  onNewChat: () => void;
  onRename: (id: string, newTitle: string) => void;
  onDelete: (id: string) => void;
  isLoading?: boolean;
}

export default function ConversationSidebar({
  conversations,
  activeId,
  onSelect,
  isOpen,
  onClose,
  onNewChat,
  onRename,
  onDelete,
  isLoading,
}: Props) {
  const [menuOpenId, setMenuOpenId] = useState<string | null>(null);
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState('');
  const renameInputRef = useRef<HTMLInputElement>(null);

  const handleRenameStart = (conv: Conversation) => {
    setRenamingId(conv.id);
    setRenameValue(conv.title);
    setMenuOpenId(null);
    setTimeout(() => renameInputRef.current?.focus(), 50);
  };

  const handleRenameSubmit = (id: string) => {
    const trimmed = renameValue.trim();
    if (trimmed && trimmed.length > 0) {
      onRename(id, trimmed);
    }
    setRenamingId(null);
    setRenameValue('');
  };

  const handleDeleteClick = (id: string) => {
    setMenuOpenId(null);
    onDelete(id);
  };

  return (
    <>
      {isOpen && (
        <div
          className="fixed inset-0 bg-foreground/20 z-40 md:hidden fade-in"
          onClick={onClose}
        />
      )}

      <div
        className={`
          fixed md:static z-50 md:z-auto
          top-0 left-0 h-full
          w-72 bg-card border-r border-border
          flex flex-col
          transition-transform duration-300 ease-spring
          ${isOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'}
          md:flex
        `}
      >
        <div className="flex items-center justify-between px-5 pt-12 pb-4 border-b border-border">
          <h2 className="text-base font-bold text-foreground">Conversations</h2>
          <div className="flex items-center gap-2">
            <button
              onClick={onNewChat}
              className="w-8 h-8 rounded-xl bg-primary/10 flex items-center justify-center text-primary hover:bg-primary/20 transition-colors"
              title="New conversation"
            >
              <Plus size={16} />
            </button>
            <button
              onClick={onClose}
              className="w-8 h-8 rounded-xl flex items-center justify-center text-muted-foreground hover:bg-muted transition-colors md:hidden"
            >
              <X size={16} />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto py-2 scrollbar-hide">
          {isLoading ? (
            <div className="px-4 py-3 space-y-3">
              {[1, 2, 3].map((i) => (
                <div key={i} className="flex items-start gap-3 animate-pulse">
                  <div className="w-9 h-9 rounded-xl bg-muted flex-shrink-0" />
                  <div className="flex-1 space-y-2">
                    <div className="h-3 bg-muted rounded w-3/4" />
                    <div className="h-2.5 bg-muted rounded w-full" />
                  </div>
                </div>
              ))}
            </div>
          ) : conversations.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full px-4 text-center">
              <MessageCircleHeart size={32} className="text-muted-foreground mb-2" />
              <p className="text-sm text-muted-foreground">No conversations yet.</p>
              <p className="text-xs text-muted-foreground mt-1">Start chatting to see them here.</p>
            </div>
          ) : (
            conversations.map((conv) => (
              <div key={conv.id} className="relative group">
                {renamingId === conv.id ? (
                  <div className="flex items-center gap-2 px-4 py-3">
                    <div className="w-9 h-9 rounded-xl lavender-gradient flex items-center justify-center flex-shrink-0">
                      <MessageCircleHeart size={16} className="text-white" />
                    </div>
                    <input
                      ref={renameInputRef}
                      value={renameValue}
                      onChange={(e) => setRenameValue(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') handleRenameSubmit(conv.id);
                        if (e.key === 'Escape') { setRenamingId(null); setRenameValue(''); }
                      }}
                      className="flex-1 text-sm font-semibold text-foreground bg-input border border-primary rounded-xl px-2 py-1 outline-none min-w-0"
                      maxLength={60}
                    />
                    <button
                      onClick={() => handleRenameSubmit(conv.id)}
                      className="w-7 h-7 rounded-lg bg-primary/10 flex items-center justify-center text-primary hover:bg-primary/20 flex-shrink-0"
                    >
                      <Check size={13} />
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={() => { onSelect(conv.id); setMenuOpenId(null); }}
                    className={`w-full flex items-start gap-3 px-4 py-3.5 text-left transition-all duration-150 hover:bg-muted/60 ${
                      activeId === conv.id ? 'bg-primary/8' : ''
                    }`}
                  >
                    <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 mt-0.5 ${
                      activeId === conv.id ? 'lavender-gradient' : 'bg-muted'
                    }`}>
                      <MessageCircleHeart
                        size={16}
                        className={activeId === conv.id ? 'text-white' : 'text-muted-foreground'}
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <p className={`text-sm font-semibold truncate ${
                          activeId === conv.id ? 'text-primary' : 'text-foreground'
                        }`}>
                          {conv.title}
                        </p>
                        <span className="text-xs text-muted-foreground flex-shrink-0 ml-2">{conv.time}</span>
                      </div>
                      <p className="text-xs text-muted-foreground truncate mt-0.5">{conv.preview}</p>
                    </div>
                  </button>
                )}

                {/* Context menu button */}
                {renamingId !== conv.id && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setMenuOpenId(menuOpenId === conv.id ? null : conv.id);
                    }}
                    className="absolute right-2 top-1/2 -translate-y-1/2 w-7 h-7 rounded-lg flex items-center justify-center text-muted-foreground hover:bg-muted opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity"
                    aria-label="Conversation options"
                  >
                    <MoreHorizontal size={15} />
                  </button>
                )}

                {/* Dropdown menu */}
                {menuOpenId === conv.id && (
                  <>
                    <div className="fixed inset-0 z-10" onClick={() => setMenuOpenId(null)} />
                    <div className="absolute right-2 top-12 z-20 bg-card border border-border rounded-xl shadow-lg overflow-hidden min-w-[140px]">
                      <button
                        onClick={() => { onSelect(conv.id); setMenuOpenId(null); onClose(); }}
                        className="w-full flex items-center gap-2.5 px-3.5 py-2.5 text-sm text-foreground hover:bg-muted/60 transition-colors"
                      >
                        <ArrowRight size={14} className="text-primary" />
                        Continue
                      </button>
                      <div className="h-px bg-border/50 mx-2" />
                      <button
                        onClick={() => handleRenameStart(conv)}
                        className="w-full flex items-center gap-2.5 px-3.5 py-2.5 text-sm text-foreground hover:bg-muted/60 transition-colors"
                      >
                        <Pencil size={14} className="text-muted-foreground" />
                        Rename
                      </button>
                      <div className="h-px bg-border/50 mx-2" />
                      <button
                        onClick={() => handleDeleteClick(conv.id)}
                        className="w-full flex items-center gap-2.5 px-3.5 py-2.5 text-sm text-red-500 hover:bg-red-50 transition-colors"
                      >
                        <Trash2 size={14} />
                        Delete
                      </button>
                    </div>
                  </>
                )}
              </div>
            ))
          )}
        </div>

        <div className="px-4 py-4 border-t border-border">
          <p className="text-xs text-muted-foreground text-center leading-relaxed">
            Your conversations are private and only visible to you.
          </p>
        </div>
      </div>
    </>
  );
}